import React, { useState, useEffect } from 'react';
import { Mail, CheckCheck, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { parseServerDate } from '../utils/serverDate';

// Renders a received email reply. All email text is rendered as escaped React
// text only (never HTML). Attachments are shown by name only.
const EmailReplyItem = ({ message, email, onMarkDnc, dncMarked, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const when = parseServerDate(message?.response_timestamp);
  const attachments = Array.isArray(email?.attachments) ? email.attachments : [];
  const fullText = email?.body_text;

  // Auto-revert the confirm step after a few seconds
  useEffect(() => {
    if (!confirming) return undefined;
    const t = setTimeout(() => setConfirming(false), 5000);
    return () => clearTimeout(t);
  }, [confirming]);

  const dncDone = !!dncMarked;

  const handleDnc = async () => {
    if (!onMarkDnc || busy) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    try {
      await onMarkDnc();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="message-item received">
      <div className="message-bubble">
        <div className="message-meta">
          <Mail className="w-3 h-3" />
          <span>{when ? format(when, 'MMM d, h:mm a') : ''}</span>
          {!!message?.is_read && <CheckCheck className="w-3 h-3" />}
          {email?.is_auto_reply && <span className="badge email-badge">Auto-reply</span>}
          {onDelete && (
            <button type="button" className="message-delete" onClick={onDelete} title="Delete message" aria-label="Delete message">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        {email?.subject ? <div className="email-reply-subject">{String(email.subject)}</div> : null}
        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message?.response_content ?? ''}</p>

        {email?.suggest_dnc && (
          <div className="email-dnc-suggest">
            <span className="badge email-badge warn">Looks like an unsubscribe request</span>
            <button
              type="button"
              className="action-button"
              onClick={handleDnc}
              disabled={dncDone || busy}
            >
              {dncDone
                ? 'Marked Do Not Contact'
                : confirming
                  ? 'Confirm: mark Do Not Contact'
                  : 'Mark Do Not Contact'}
            </button>
            {confirming && !dncDone && !busy && (
              <button type="button" className="action-button" onClick={() => setConfirming(false)}>
                Cancel
              </button>
            )}
          </div>
        )}

        {email && fullText ? (
          <div className="email-full">
            <button
              type="button"
              className="email-toggle"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Hide full message' : 'Show full message'}
            </button>
            {expanded && (
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{String(fullText)}</pre>
            )}
          </div>
        ) : null}

        {attachments.length > 0 && (
          <ul className="email-attachments">
            {attachments.map((att, i) => (
              <li key={i}>Attachment: {String(att?.name ?? att?.filename ?? 'file')}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EmailReplyItem;
